import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SubmittedActionCard from '../components/SubmittedActionCard';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  Tab,
  Typography,
} from '@mui/material';
import { TabPanel, TabContext, TabList } from '@mui/lab';
import { API } from '../services/amplifyCompat';
import {
  getAllSubmittedActionsForUser,
} from '../graphql/queries';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import useTranslation from './customHooks/translations';
import { useLanguageContext } from './contexts/LanguageContext';
import { useUserInfoContext } from '../hooks/use-user-info-context';

const UserActions = ({ databaseUser, userType }) => {
  const [showMore, setShowMore] = useState({
    validated: false,
    unvalidated: false,
    failed: false,
  });
  const [validatedActions, setValidatedActions] = useState();
  const [unvalidatedActions, setUnvalidatedActions] = useState();
  const [failedActions, setFailedActions] = useState();
  const [actionFeedback, setActionFeedback] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const translation = useTranslation();
  const validatedTabLabel = translation.validated
    ? `${translation.validated.charAt(0).toUpperCase()}${translation.validated.slice(1)}`
    : '';
  const tabs = useMemo(
    () => [
      validatedTabLabel,
      translation.awaitingValidation,
      translation.notPassValidation,
    ],
    [translation.awaitingValidation, translation.notPassValidation, validatedTabLabel]
  );
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguageContext();
  const { user: currentUser } = useUserInfoContext();

  const canDeleteOwnActions =
    databaseUser?.user_id !== undefined &&
    currentUser?.user_id !== undefined &&
    Number(databaseUser.user_id) === Number(currentUser.user_id);

  const refreshUserActions = useCallback(async () => {
    if (!databaseUser?.user_id) {
      return;
    }

    const actionRes = await API.graphql({
      query: getAllSubmittedActionsForUser,
      variables: { user_id: databaseUser.user_id },
    });
    const allActions = actionRes.data.getAllSubmittedActionsForUser;

    setValidatedActions(allActions.filter((action) => action.is_validated));
    setUnvalidatedActions(
      allActions.filter((action) => !action.is_validated && !action.is_rejected)
    );
    setFailedActions(allActions.filter((action) => action.is_rejected));
  }, [databaseUser?.user_id]);

  const handleActionFeedback = useCallback(({ severity = 'success', message }) => {
    setActionFeedback({
      open: true,
      severity,
      message,
    });
  }, []);

  useEffect(() => {
    if (databaseUser) {
      refreshUserActions();
    }

    setSelectedTab(tabs[0]);
  }, [databaseUser, refreshUserActions, tabs, language]);

  const renderActionCards = (actions, emptyMessage, tabKey, containerHeight = '100%') => {
    if (Array.isArray(actions) && actions.length !== 0) {
      const visibleActions = showMore[tabKey] ? actions : actions.slice(0, 3);

      return (
        <Box
          sx={{
            height: containerHeight,
            overflow: 'auto',
            padding: '0.25em',
          }}
        >
          <Stack spacing={2}>
            {visibleActions.map((action) => (
              <SubmittedActionCard
                key={action.sa_id}
                action={action}
                showUnapproveButton={userType === 'Admin'}
                showDeleteButton={canDeleteOwnActions}
                refreshUserActions={refreshUserActions}
                onActionFeedback={handleActionFeedback}
              />
            ))}
            {actions.length > 3 && (
              <Button
                sx={{ mt: '3em' }}
                variant="outlined"
                onClick={() =>
                  setShowMore((prev) => ({
                    ...prev,
                    [tabKey]: !prev[tabKey],
                  }))
                }
              >
                {translation.view} {showMore[tabKey] ? translation.less : translation.more}
              </Button>
            )}
          </Stack>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography component="p" variant="subtitle2">
          {emptyMessage}
        </Typography>
      </Box>
    );
  };

  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
  };

  const handleFeedbackClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setActionFeedback((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <>
      <Box sx={{ textAlign: { xs: 'center' } }}>
        <TabContext value={tabs.indexOf(selectedTab) !== -1 ? selectedTab : tabs[0]}>
          <Box
            sx={{
              borderBottom: 1,
              borderTop: 1,
              borderColor: 'divider',
              maxWidth: { sm: '100%' },
            }}
          >
            <TabList
              onChange={handleTabChange}
              aria-label="view user action tabs"
              variant="fullWidth"
              orientation={mobileView ? 'vertical' : 'horizontal'}
            >
              <Tab label={tabs[0]} value={tabs[0]} />
              <Tab label={tabs[1]} value={tabs[1]} />
              <Tab label={tabs[2]} value={tabs[2]} />
            </TabList>
          </Box>
          <TabPanel
            value={tabs[0]}
            sx={{
              height: { xs: '500px', md: 'auto' },
            }}
          >
            {renderActionCards(
              validatedActions,
              translation.noValidatedActions,
              'validated'
            )}
          </TabPanel>
          <TabPanel
            value={tabs[1]}
            sx={{
              height: { xs: '500px', md: 'auto' },
            }}
          >
            {renderActionCards(
              unvalidatedActions,
              translation.noUnvalidatedActions,
              'unvalidated'
            )}
          </TabPanel>
          <TabPanel
            value={tabs[2]}
            sx={{
              height: { xs: '500px', md: 'auto' },
            }}
          >
            {renderActionCards(
              failedActions,
              translation.noFailedActions,
              'failed',
              '110vh'
            )}
          </TabPanel>
        </TabContext>
      </Box>
      <Snackbar
        open={actionFeedback.open}
        autoHideDuration={4000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={actionFeedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {actionFeedback.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserActions;
